import { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Phone, Delete, User, Building2 } from 'lucide-react';
import { useContactSearch, type ContactSearchResult } from '@/hooks/useContactSearch';
import { type CallerIdOption } from '@/hooks/useCallerIdOptions';

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

export interface CallMeta {
  contactId?: string;
  leadId?: string;
  accountId?: string;
  callerIdNumberId?: string;
}

interface DialPadProps {
  onCall: (number: string, meta?: CallMeta) => void;
  callerIdOptions?: CallerIdOption[];
  selectedCallerId?: string | null;
  onCallerIdChange?: (id: string) => void;
}

export function DialPad({ onCall, callerIdOptions, selectedCallerId, onCallerIdChange }: DialPadProps) {
  const [input, setInput] = useState('');
  const [selectedContact, setSelectedContact] = useState<ContactSearchResult | null>(null);

  const isNameSearch = useMemo(() => /[a-zA-Z]/.test(input), [input]);
  const searchQuery = isNameSearch ? input : '';
  const { data: searchResults = [] } = useContactSearch(searchQuery);

  const handleKey = useCallback((key: string) => {
    setInput(prev => prev + key);
    setSelectedContact(null);
  }, []);

  const handleBackspace = useCallback(() => {
    setInput(prev => prev.slice(0, -1));
    setSelectedContact(null);
  }, []);

  const handleSelectContact = useCallback((contact: ContactSearchResult) => {
    setInput(contact.phone);
    setSelectedContact(contact);
  }, []);

  const handleCall = useCallback(() => {
    const number = input.trim();
    if (!number) return;

    const meta: CallMeta = {};
    if (selectedCallerId) meta.callerIdNumberId = selectedCallerId;
    if (selectedContact) {
      if (selectedContact.entityType === 'contact') {
        meta.contactId = selectedContact.id;
        meta.accountId = selectedContact.accountId || undefined;
      } else {
        meta.leadId = selectedContact.id;
      }
    }

    onCall(number, meta);
    setInput('');
    setSelectedContact(null);
  }, [input, selectedCallerId, selectedContact, onCall]);

  return (
    <div className="p-3 space-y-2">
      {/* Caller ID selector */}
      {callerIdOptions && callerIdOptions.length > 0 && (
        <Select value={selectedCallerId || undefined} onValueChange={onCallerIdChange}>
          <SelectTrigger className="h-8 text-xs">
            <span className="text-muted-foreground mr-1">From:</span>
            <SelectValue placeholder="Select number" />
          </SelectTrigger>
          <SelectContent>
            {callerIdOptions.map(opt => (
              <SelectItem key={opt.id} value={opt.id} className="text-xs">
                <span className="font-mono">{opt.friendlyName || opt.phoneNumber}</span>
                {opt.isAssigned && <span className="text-muted-foreground ml-1">(yours)</span>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Number/search input */}
      <div className="relative">
        <div className="flex items-center gap-1">
          <Input
            value={input}
            onChange={(e) => { setInput(e.target.value); setSelectedContact(null); }}
            placeholder="Number or name..."
            className="font-mono text-center text-lg h-10"
            onKeyDown={(e) => e.key === 'Enter' && !isNameSearch && handleCall()}
          />
          {input && (
            <Button size="icon" variant="ghost" className="h-10 w-10 shrink-0" onClick={handleBackspace}>
              <Delete className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Contact search results */}
        {isNameSearch && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
            {searchResults.map(r => (
              <button
                key={`${r.entityType}-${r.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={() => handleSelectContact(r)}
              >
                {r.entityType === 'contact' ? (
                  <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                ) : (
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{r.phone}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-1">
        {KEYS.map((row) =>
          row.map((key) => (
            <Button
              key={key}
              variant="outline"
              className="h-10 text-lg font-mono"
              onClick={() => handleKey(key)}
            >
              {key}
            </Button>
          ))
        )}
      </div>

      {/* Call button */}
      <Button
        className="w-full h-10 bg-green-600 hover:bg-green-700 text-white"
        onClick={handleCall}
        disabled={!input.trim() || isNameSearch}
      >
        <Phone className="h-4 w-4 mr-2" />
        Call
      </Button>
    </div>
  );
}
